import type { StyleRun } from '@rta/rta-blueprint-library';

/** A partial style override: a value sets the property, `null` removes it (inherit the box scalar), absent leaves it untouched */
export type StyleRunPatch = {
	color?: string | null;
	backgroundColor?: string | null;
	fontWeight?: string | null;
	fontStyle?: string | null;
	textDecoration?: string | null;
	fontSize?: number | null;
	fontFamily?: string | null;
};

export const STYLE_RUN_PROPERTIES: Array<keyof StyleRunPatch> = ['color', 'backgroundColor', 'fontWeight', 'fontStyle', 'textDecoration', 'fontSize', 'fontFamily'];

export const isSameStyleRun = (first: StyleRun, second: StyleRun): boolean =>
	STYLE_RUN_PROPERTIES.every((property) => first[property] === second[property]);

/** The same patch with every property it sets turned into a removal -- what a whole-box format has to do to the runs it overrides */
export const nullifyStyleRunPatch = (patch: StyleRunPatch): StyleRunPatch => {
	const cleared: Record<string, null> = {};
	STYLE_RUN_PROPERTIES.forEach((property) => { if (typeof patch[property] !== 'undefined') cleared[property] = null; });
	return cleared as StyleRunPatch;
};

const hasAnyStyle = (run: StyleRun): boolean => STYLE_RUN_PROPERTIES.some((property) => typeof run[property] !== 'undefined');

/** Enforces the run invariant: sorted by start, no zero-length or style-less runs, adjacent identical runs merged */
export const normalizeStyleRuns = (runs: Array<StyleRun>): Array<StyleRun> => {
	const cleaned = runs
		.filter((run) => run.end > run.start && hasAnyStyle(run))
		.sort((first, second) => (first.start - second.start) || (first.end - second.end));

	const merged: Array<StyleRun> = [];
	cleaned.forEach((run) => {
		const previous = merged[merged.length - 1];
		if (previous && previous.end === run.start && isSameStyleRun(previous, run)) {
			previous.end = run.end;
			return;
		}
		merged.push({ ...run });
	});

	return merged;
};

const mergePatch = (run: StyleRun, patch: StyleRunPatch): StyleRun => {
	const merged: StyleRun = { ...run };
	const target: Record<string, unknown> = merged;
	STYLE_RUN_PROPERTIES.forEach((property) => {
		const value = patch[property];
		if (typeof value === 'undefined') return;
		if (value === null) delete target[property];
		else target[property] = value;
	});
	return merged;
};

export type ApplyStyleRunParams = {
	runs: Array<StyleRun>;
	start: number;
	end: number;
	patch: StyleRunPatch;
};

/**
 * Applies a partial style over [start, end), MERGING with whatever the overlapped runs already carry --
 * bolding a range that is already red yields red+bold. Portions of the overlapped runs that fall outside
 * the range keep their original style, and gaps inside the range take the patch alone (#390).
 */
export const applyStyleRun = (params: ApplyStyleRunParams): Array<StyleRun> => {
	const { runs, start, end, patch } = params;
	if (end <= start) return normalizeStyleRuns(runs);

	const result: Array<StyleRun> = [];
	const covered: Array<{ start: number; end: number }> = [];

	runs.forEach((existing) => {
		if (existing.end <= start || existing.start >= end) {
			result.push({ ...existing });
			return;
		}
		if (existing.start < start) result.push({ ...existing, end: start });
		if (existing.end > end) result.push({ ...existing, start: end });

		const overlapStart = Math.max(existing.start, start);
		const overlapEnd = Math.min(existing.end, end);
		covered.push({ start: overlapStart, end: overlapEnd });
		result.push(mergePatch({ ...existing, start: overlapStart, end: overlapEnd }, patch));
	});

	let cursor = start;
	covered.sort((first, second) => first.start - second.start).forEach((range) => {
		if (range.start > cursor) result.push(mergePatch({ start: cursor, end: range.start }, patch));
		cursor = Math.max(cursor, range.end);
	});
	if (cursor < end) result.push(mergePatch({ start: cursor, end }, patch));

	return normalizeStyleRuns(result);
};
